import { useState, useEffect } from 'react';

interface GeoLocationResult {
    country: string | null;
    countryCode: string | null;
    loading: boolean;
    error: string | null;
    isAlgeria: boolean;
}

const CACHE_KEY = 'futurasmile_geo_cache';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

interface CachedGeoData {
    country: string;
    countryCode: string;
    timestamp: number;
}

/**
 * Hook to detect user's country using backend geo-detection API.
 * The backend calls ip-api.com server-side to avoid CORS issues.
 * Caches result in localStorage for 24 hours.
 * 
 * @returns GeoLocationResult with country info and isAlgeria boolean
 */
export function useGeoLocation(): GeoLocationResult {
    const [country, setCountry] = useState<string | null>(null);
    const [countryCode, setCountryCode] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchLocation = async () => {
            try {
                // Check cache first
                const cached = localStorage.getItem(CACHE_KEY);
                if (cached) {
                    const cachedData: CachedGeoData = JSON.parse(cached);
                    const isExpired = Date.now() - cachedData.timestamp > CACHE_DURATION;

                    if (!isExpired) {
                        setCountry(cachedData.country);
                        setCountryCode(cachedData.countryCode);
                        setLoading(false);
                        return;
                    }
                }

                // Fetch from our backend endpoint (no CORS issues)
                const response = await fetch('/api/geo');

                if (!response.ok) {
                    throw new Error('Failed to fetch location');
                }

                const data = await response.json();

                if (data.countryCode) {
                    setCountry(data.country);
                    setCountryCode(data.countryCode);

                    // Cache the result
                    const cacheData: CachedGeoData = {
                        country: data.country,
                        countryCode: data.countryCode,
                        timestamp: Date.now(),
                    };
                    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
                } else {
                    // Geo lookup returned null (local IP or failed)
                    setCountry(null);
                    setCountryCode(null);
                }
            } catch (err) {
                console.warn('Geolocation detection failed:', err);
                setError(err instanceof Error ? err.message : 'Unknown error');
                // Don't block the UI, just default to international
            } finally {
                setLoading(false);
            }
        };

        fetchLocation();
    }, []);

    return {
        country,
        countryCode,
        loading,
        error,
        isAlgeria: countryCode === 'DZ',
    };
}

/**
 * For testing purposes: manually set the geo cache
 * Call this from browser console: setGeoCache('DZ', 'Algeria')
 */
if (typeof window !== 'undefined') {
    (window as any).setGeoCache = (code: string, name: string) => {
        const cacheData: CachedGeoData = {
            country: name,
            countryCode: code,
            timestamp: Date.now(),
        };
        localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
        console.log(`Geo cache set to ${name} (${code}). Refresh the page to see changes.`);
    };

    (window as any).clearGeoCache = () => {
        localStorage.removeItem(CACHE_KEY);
        console.log('Geo cache cleared. Refresh the page to detect location again.');
    };
}

export default useGeoLocation;
